import { html, unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { TimePreset, TimeSelection } from '@/common/types'
import { DdsElement } from '@/internal/dds-hu-element'
import { getTimeFormatPreference, t } from '@/localization/localization'

import styles from './time-grid.scss?inline'
/**
 * `dap-ds-time-grid`
 * @summary A time grid component that allows users to select hours and minutes in two side-by-side scrollable columns.
 * @element dap-ds-time-grid
 * @title - Time grid
 * @group timepicker
 *
 * @property {number} selectedHour - The currently selected hour (0-23).
 * @property {number} selectedMinute - The currently selected minute (0-59).
 * @property {string} minTime - The minimum selectable time (format: 'HH:mm').
 * @property {string} maxTime - The maximum selectable time (format: 'HH:mm').
 * @property {number} step - The step interval in minutes.
 * @property {string} locale - The locale for time formatting.
 *
 * @event {{ detail: TimeSelection }} dds-change - Fired when time selection changes.
 * @event {{ void }} dds-close - Fired when the time grid should close.
 *
 * @csspart time-grid - The main time grid container.
 * @csspart hour-section - The hour selection section.
 * @csspart minute-section - The minute selection section.
 * @csspart time-button - Individual time selection buttons.
 * @csspart time-button-base - The base part of the time button.
 * @csspart time-button-content - The content part of the time button.
 * @csspart time-button-high-contrast - The high contrast part of the time button.
 * @csspart section-header-title - The title of the section header.
 * @csspart section-header-divider - The divider between the section header and the time buttons.
 * @csspart time-column - The time column container.
 *
 * @cssprop --dds-time-grid-gap - The gap between time sections (default: var(--dds-spacing-400)).
 * @cssprop --dds-time-grid-padding - The padding around the time grid (default: var(--dds-spacing-0)).
 * @cssprop --dds-time-grid-column-gap - The gap between time buttons in columns (default: var(--dds-spacing-200)).
 * @cssprop --dds-time-grid-border-radius - The border radius of the time grid (default: var(--dds-radius-base)).
 * @cssprop --dds-time-grid-background - The background color of the time grid (default: var(--dds-background-neutral-subtle)).
 * @cssprop --dds-time-grid-transition - The transition timing for the time grid (default: all 0.2s ease-in-out).
 * @cssprop --dds-time-grid-max-width - The maximum width of the time grid (default: 400px).
 * @cssprop --dds-time-grid-column-max-height - The maximum height of time columns (default: 240px).
 */
@customElement('dap-ds-time-grid')
export default class DapDSTimeGrid extends DdsElement {
  @property({ type: Number }) selectedHour = 12
  @property({ type: Number }) selectedMinute = 0
  @property({ type: Number }) selectedSecond = 0
  @property({ type: String }) minTime = '00:00'
  @property({ type: String }) maxTime = '23:59'
  @property({ type: Number }) step = 5
  @property({ type: String }) locale = 'en'
  @property({ type: Boolean }) showSeconds = false
  @property({ type: Number }) secondsStep = 1
  @property({ attribute: false }) disabledTime?: (
    hour: number,
    minute: number,
    second?: number,
  ) => boolean
  @property({ type: Boolean }) hideDisabledTimes = false
  @property({ type: Array }) presets?: TimePreset[]

  static readonly styles = unsafeCSS(styles)

  private getHours(): number[] {
    return Array.from({ length: 24 }, (_, i) => i)
  }

  private getMinutes(): number[] {
    const minutes = []
    for (let i = 0; i < 60; i += this.step) {
      minutes.push(i)
    }
    return minutes
  }

  private getSeconds(): number[] {
    const seconds = []
    for (let i = 0; i < 60; i += this.secondsStep) {
      seconds.push(i)
    }
    return seconds
  }

  private isTimeDisabled(
    hour: number,
    minute: number,
    second?: number,
  ): boolean {
    // Check min/max time range
    const timeStr = this.showSeconds
      ? `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${(second ?? 0).toString().padStart(2, '0')}`
      : `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`

    const minTimeStr = this.minTime
    const maxTimeStr = this.maxTime

    const isOutOfRange = timeStr < minTimeStr || timeStr > maxTimeStr

    // Check custom disabled callback
    if (this.disabledTime) {
      return isOutOfRange || this.disabledTime(hour, minute, second)
    }

    return isOutOfRange
  }

  private handleHourSelect(hour: number) {
    this.selectedHour = hour
    this.requestUpdate()
    this.emitChange()
  }

  private handleMinuteSelect(minute: number) {
    this.selectedMinute = minute
    this.requestUpdate()
    this.emitChange()
  }

  private handleSecondSelect(second: number) {
    this.selectedSecond = second
    this.requestUpdate()
    this.emitChange()
  }

  private emitChange() {
    const changeData: TimeSelection = {
      hour: this.selectedHour,
      minute: this.selectedMinute,
      ...(this.showSeconds && { second: this.selectedSecond }),
    }
    this.emit('dds-change', changeData)
  }

  private formatHour(hour: number): string {
    const timeFormat = getTimeFormatPreference(this.locale)

    if (timeFormat === '24h') {
      return hour.toString().padStart(2, '0')
    }

    // 12-hour format
    if (hour === 0) return '12 AM'
    if (hour === 12) return '12 PM'
    if (hour > 12) return `${hour - 12} PM`
    return `${hour} AM`
  }

  private formatMinute(minute: number): string {
    return minute.toString().padStart(2, '0')
  }

  private formatSecond(second: number): string {
    return second.toString().padStart(2, '0')
  }

  private handlePresetSelect(preset: TimePreset) {
    // Parse the preset value and set the time
    const timeParts = preset.value.split(':')
    const hour = parseInt(timeParts[0], 10)
    const minute = parseInt(timeParts[1], 10)
    const second = timeParts[2] ? parseInt(timeParts[2], 10) : 0

    this.selectedHour = hour
    this.selectedMinute = minute
    if (this.showSeconds) {
      this.selectedSecond = second
    }

    this.requestUpdate()
    this.emitChange()
  }

  private isPresetDisabled(preset: TimePreset): boolean {
    const timeParts = preset.value.split(':')
    const hour = parseInt(timeParts[0], 10)
    const minute = parseInt(timeParts[1], 10)
    const second = timeParts[2] ? parseInt(timeParts[2], 10) : undefined

    return this.isTimeDisabled(hour, minute, second)
  }

  public scrollToSelectedTime() {
    // Scroll to selected hour
    const hourColumn = this.shadowRoot?.querySelector('#hour-column')
    if (hourColumn) {
      const hourButtons = hourColumn.querySelectorAll('dap-ds-button')
      const selectedHourButton = Array.from(hourButtons).find(
        button =>
          button.textContent?.trim() === this.formatHour(this.selectedHour),
      )

      if (selectedHourButton) {
        selectedHourButton.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        })
      }
    }

    // Scroll to selected minute
    const minuteColumn = this.shadowRoot?.querySelector('#minute-column')
    if (minuteColumn) {
      const minuteButtons = minuteColumn.querySelectorAll('dap-ds-button')
      const selectedMinuteButton = Array.from(minuteButtons).find(
        button =>
          button.textContent?.trim() === this.formatMinute(this.selectedMinute),
      )

      if (selectedMinuteButton) {
        selectedMinuteButton.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        })
      }
    }

    // Scroll to selected second (if seconds are enabled)
    if (this.showSeconds) {
      const secondColumn = this.shadowRoot?.querySelector('#second-column')
      if (secondColumn) {
        const secondButtons = secondColumn.querySelectorAll('dap-ds-button')
        const selectedSecondButton = Array.from(secondButtons).find(
          button =>
            button.textContent?.trim() ===
            this.formatSecond(this.selectedSecond),
        )

        if (selectedSecondButton) {
          selectedSecondButton.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          })
        }
      }
    }
  }

  override firstUpdated(changedProperties: Map<string, unknown>) {
    super.firstUpdated(changedProperties)
    // Scroll to selected time after first render
    requestAnimationFrame(() => {
      this.scrollToSelectedTime()
    })
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    // Scroll to selected time when selection changes
    if (
      changedProperties.has('selectedHour') ||
      changedProperties.has('selectedMinute') ||
      changedProperties.has('selectedSecond')
    ) {
      requestAnimationFrame(() => {
        this.scrollToSelectedTime()
      })
    }
  }

  render() {
    const hours = this.getHours()
    const minutes = this.getMinutes()
    const seconds = this.showSeconds ? this.getSeconds() : []

    // Filter hours, minutes, and seconds based on hideDisabledTimes
    const filteredHours = this.hideDisabledTimes
      ? hours.filter(
          hour =>
            !this.isTimeDisabled(
              hour,
              this.selectedMinute,
              this.selectedSecond,
            ),
        )
      : hours

    const filteredMinutes = this.hideDisabledTimes
      ? minutes.filter(
          minute =>
            !this.isTimeDisabled(
              this.selectedHour,
              minute,
              this.selectedSecond,
            ),
        )
      : minutes

    const filteredSeconds = this.hideDisabledTimes
      ? seconds.filter(
          second =>
            !this.isTimeDisabled(
              this.selectedHour,
              this.selectedMinute,
              second,
            ),
        )
      : seconds

    return html`
      <div part="time-grid" class="time-grid">
        ${this.presets && this.presets.length > 0
          ? html`
              <div part="presets-section" class="presets-section">
                ${this.presets.map(preset => {
                  const isDisabled = this.isPresetDisabled(preset)
                  return html`
                    <dap-ds-button
                      variant="outline"
                      size="xs"
                      part="preset-button"
                      ?disabled=${isDisabled}
                      @click=${() => this.handlePresetSelect(preset)}>
                      ${preset.label}
                    </dap-ds-button>
                  `
                })}
              </div>
              <dap-ds-divider spacing="200"></dap-ds-divider>
            `
          : ''}

        <div class="time-columns">
          <div part="hour-section" class="time-section">
            <div class="section-header">
              <span part="section-header-title"
                >${t('timepicker.hour', {
                  lng: this.locale,
                })}</span
              >
              <dap-ds-divider
                part="section-header-divider"
                spacing="200"></dap-ds-divider>
            </div>
            <div part="time-column" class="time-column" id="hour-column">
              ${filteredHours.map(hour => {
                const isDisabled = this.isTimeDisabled(
                  hour,
                  this.selectedMinute,
                  this.selectedSecond,
                )
                const isSelected = hour === this.selectedHour
                return html`
                  <dap-ds-button
                    variant="subtle-menu"
                    ?active=${isSelected}
                    part="time-button"
                    exportparts="base:time-button-base, content:time-button-content, high-contrast:time-button-high-contrast"
                    ?disabled=${isDisabled}
                    @click=${() => this.handleHourSelect(hour)}>
                    ${this.formatHour(hour)}
                  </dap-ds-button>
                `
              })}
            </div>
          </div>

          <div part="minute-section" class="time-section">
            <div class="section-header">
              <span part="section-header-title"
                >${t('timepicker.minute', {
                  lng: this.locale,
                })}</span
              >
              <dap-ds-divider
                part="section-header-divider"
                spacing="200"></dap-ds-divider>
            </div>
            <div part="time-column" class="time-column" id="minute-column">
              ${filteredMinutes.map(minute => {
                const isDisabled = this.isTimeDisabled(
                  this.selectedHour,
                  minute,
                  this.selectedSecond,
                )
                const isSelected = minute === this.selectedMinute
                return html`
                  <dap-ds-button
                    variant="subtle-menu"
                    ?active=${isSelected}
                    part="time-button"
                    ?disabled=${isDisabled}
                    @click=${() => this.handleMinuteSelect(minute)}>
                    ${this.formatMinute(minute)}
                  </dap-ds-button>
                `
              })}
            </div>
          </div>

          ${this.showSeconds
            ? html`
                <div part="second-section" class="time-section">
                  <div class="section-header">
                    <span part="section-header-title"
                      >${t('timepicker.second', {
                        lng: this.locale,
                      })}</span
                    >
                    <dap-ds-divider
                      part="section-header-divider"
                      spacing="200"></dap-ds-divider>
                  </div>
                  <div
                    part="time-column"
                    class="time-column"
                    id="second-column">
                    ${filteredSeconds.map(second => {
                      const isDisabled = this.isTimeDisabled(
                        this.selectedHour,
                        this.selectedMinute,
                        second,
                      )
                      const isSelected = second === this.selectedSecond
                      return html`
                        <dap-ds-button
                          variant="subtle-menu"
                          ?active=${isSelected}
                          part="time-button"
                          ?disabled=${isDisabled}
                          @click=${() => this.handleSecondSelect(second)}>
                          ${this.formatSecond(second)}
                        </dap-ds-button>
                      `
                    })}
                  </div>
                </div>
              `
            : ''}
        </div>
      </div>
    `
  }
}
